import { useEffect, useRef } from 'react';
import { DocumentMask, FaceMask, faBan, faCamera } from '~/assets';
import { If, SceneWrapper } from '~/components';
import { MaskType, useCamera } from '~/utils';
import {
  CameraIcon,
  CaptureButton,
  CustomFooter,
  ErrorIcon,
  MaskOverlay,
  Message,
  MessageContainer,
  NextButton,
  PreviewContainer,
  PreviewImage,
  RetryButton,
  SceneTitle,
  Title,
  Video,
  VideoContainer,
} from './styles';

interface TakePictureProps {
  handleSubmit: (image: string) => Promise<void>;
  sceneTitle?: string;
  allowFrontCamera?: boolean;
  maskType?: MaskType;
}

const TakePicture = ({
  handleSubmit,
  sceneTitle,
  allowFrontCamera = true,
  maskType,
}: TakePictureProps) => {
  const {
    hasPermission,
    isFrontCamera,
    capturedImage,
    videoRef,
    cameraError,
    capturePhoto,
    handleTryAgain,
    startCamera,
    setupVideo,
    stream,
    stopCamera,
  } = useCamera();

  const isInitializedRef = useRef(false);
  const currentFacingModeRef = useRef<'user' | 'environment'>('user');

  useEffect(() => {
    const facingMode = allowFrontCamera ? 'user' : 'environment';

    // Só inicia se não foi inicializado ou se o modo mudou
    if (
      !isInitializedRef.current ||
      currentFacingModeRef.current !== facingMode
    ) {
      currentFacingModeRef.current = facingMode;
      isInitializedRef.current = true;

      // Delay para garantir que o cleanup anterior foi completado
      setTimeout(() => {
        startCamera(facingMode);
      }, 100);
    }

    return () => {
      stopCamera();
      isInitializedRef.current = false;
    };
  }, [allowFrontCamera]);

  useEffect(() => {
    if (stream) {
      setupVideo();
    }
  }, [stream]);

  const renderFooter = () => {
    if (hasPermission === true && !capturedImage) {
      return (
        <CaptureButton id="capture-button" onPress={capturePhoto} fullWidth>
          <CameraIcon icon={faCamera} />
          CAPTURAR FOTO
        </CaptureButton>
      );
    }

    if (hasPermission === true && capturedImage) {
      return (
        <CustomFooter>
          <RetryButton
            id="try-again-button"
            onPress={handleTryAgain}
            fullWidth
            transparent
          >
            <CameraIcon icon={faCamera} />
            CAPTURAR NOVA FOTO
          </RetryButton>
          <NextButton
            id="next-button"
            onPress={() => handleSubmit(capturedImage)}
            fullWidth
          >
            UTILIZAR ESSA FOTO E AVANÇAR
          </NextButton>
        </CustomFooter>
      );
    }

    if (hasPermission === false) {
      const isNotReadableError =
        cameraError?.title === 'Erro ao acessar câmera';

      return (
        <RetryButton
          id="retry-button"
          onPress={() => {
            if (isNotReadableError) {
              window.location.reload();
            } else {
              const facingMode = allowFrontCamera ? 'user' : 'environment';
              startCamera(facingMode);
            }
          }}
          fullWidth
        >
          {isNotReadableError ? 'RECARREGAR PÁGINA' : 'TENTAR NOVAMENTE'}
        </RetryButton>
      );
    }
  };

  return (
    <>
      <SceneWrapper footer={renderFooter()}>
        <If condition={hasPermission === null}>
          <MessageContainer>
            <Title>
              {cameraError?.title || 'Solicitando permissão da câmera...'}
            </Title>
            <Message>
              {cameraError?.message ||
                'Por favor, aguarde enquanto solicitamos acesso à sua câmera.'}
            </Message>
          </MessageContainer>
        </If>

        <If condition={hasPermission === false}>
          <MessageContainer>
            <ErrorIcon icon={faBan} />
            <Title>{cameraError?.title}</Title>
            <Message>{cameraError?.message}</Message>
          </MessageContainer>
        </If>

        <If condition={hasPermission === true}>
          <>
            <If condition={!!sceneTitle}>
              <SceneTitle>{sceneTitle}</SceneTitle>
            </If>
            <If condition={!!capturedImage}>
              <PreviewContainer
                $isFrontCamera={allowFrontCamera ? isFrontCamera : false}
              >
                <PreviewImage src={capturedImage!} alt="Selfie capturada" />
              </PreviewContainer>
            </If>
            <If condition={!capturedImage}>
              <VideoContainer>
                <Video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  $isFrontCamera={allowFrontCamera ? isFrontCamera : false}
                >
                  <track kind="captions" src="" />
                </Video>
                <If condition={!!maskType}>
                  <MaskOverlay>
                    {maskType === MaskType.FACE ? (
                      <FaceMask />
                    ) : (
                      <DocumentMask />
                    )}
                  </MaskOverlay>
                </If>
              </VideoContainer>
            </If>
          </>
        </If>
      </SceneWrapper>
    </>
  );
};

export default TakePicture;
